package org.teddapp.project.ui.today

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.tedd.db.Task
import org.teddapp.project.core.CoreReaction
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.TeddAmount
import org.teddapp.project.ui.TeddAmountSize
import org.teddapp.project.ui.TeddBento
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddCardTone
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddMeta
import org.teddapp.project.ui.TeddScreenHeader
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTaskItem
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.bentoSpan
import org.teddapp.project.ui.nav.Section

/**
 * Aujourd'hui, en mise en page large : une journée, un écran.
 *
 * Le salut et les deux actions du jour, puis une grille bento — les tâches (7 colonnes)
 * à côté de la recommandation (5), puis trois tuiles de 4 : Focus, Finance, le jour en
 * chiffres. Chaque module y pose une tuile ; aucun ne réclame d'attention par la couleur.
 */
@Composable
internal fun TodayBento(
    model: TodayModel,
    coreLabel: String?,
    reactions: List<CoreReaction>,
    onDismissReaction: (CoreReaction) -> Unit,
    onNavigate: (Section) -> Unit,
    deadlines: List<DeadlineReminder> = emptyList(),
    loanDeadlines: List<LoanReminder> = emptyList(),
) {
    val settings = LocalAppSettings.current
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S6)) {
        TeddScreenHeader(
            title = greetingForUser(settings.userName),
            overline = "${shortFriendlyToday()} · ${fmtTime()}",
            subtitle = todayTagline(model.pool.size),
            large = true,
        ) {
            TeddButton("Tâche", { model.quickOpen = !model.quickOpen }, icon = "plus")
            TeddButton("Session Focus", { onNavigate(Section.Focus) }, kind = TeddButtonKind.Primary, icon = "play")
        }
        if (model.quickOpen) TodayQuickCapture(model, horizontalPadding = 0.dp)
        TodayDeadlinesCard(deadlines, onOpen = { onNavigate(Section.Skills) }, loans = loanDeadlines, onOpenFinance = { onNavigate(Section.Finance) })

        TeddBento {
            TodayTasksTile(model.pool, model::toggleTask, { onNavigate(Section.Focus) }, Modifier.bentoSpan(7))
            TodayRecommendation(
                reco = model.recommendation,
                coreLabel = coreLabel,
                onStart = { onNavigate(Section.Focus) },
                onAlternate = { onNavigate(Section.Focus) },
                modifier = Modifier.bentoSpan(5),
            )
            TodayFocusTile(model, { onNavigate(Section.Focus) }, Modifier.bentoSpan(4))
            TodayFinanceTile(
                model = model,
                hidden = settings.hideBalance,
                onToggleHidden = { settings.applyHideBalance(!settings.hideBalance) },
                onOpen = { onNavigate(Section.Finance) },
                modifier = Modifier.bentoSpan(4),
            )
            TodayNumbersTile(model, { onNavigate(Section.Focus) }, { onNavigate(Section.Habits) }, Modifier.bentoSpan(4))
            if (reactions.isNotEmpty()) {
                TeddCard(Modifier.bentoSpan(12), title = "Le fil du jour", icon = "inbox", iconColor = TeddTokens.ModInbox) {
                    TodayReactionFeed(reactions = reactions, onDismiss = onDismissReaction)
                }
            }
        }
    }
}

/** Libellé lisible de l'origine d'une tâche : pas de sigle. */
private fun Task.sourceName(): String = when (parent_kind) {
    "milestone" -> "Objectif"
    "project" -> "Projet"
    else -> "Tâche libre"
}

@Composable
private fun TodayTasksTile(tasks: List<Task>, onToggle: (Task) -> Unit, onOpenFocus: () -> Unit, modifier: Modifier) {
    TeddCard(
        modifier = modifier,
        title = "Tâches du jour",
        icon = "today",
        iconColor = TeddTokens.ModToday,
        action = { TeddButton("Liste du jour", onOpenFocus, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm) },
    ) {
        if (tasks.isEmpty()) {
            TeddEmptyState(
                title = "Aucune tâche pour aujourd'hui",
                message = "Ajoute une tâche, ou choisis-en une dans tes objectifs et tes projets.",
                modifier = Modifier.fillMaxWidth(),
            )
        } else {
            Column {
                tasks.take(6).forEach { task ->
                    val left = daysLeft(task.due_date)
                    TeddTaskItem(
                        title = task.title,
                        done = task.status == "done",
                        onToggle = { onToggle(task) },
                        due = task.due_date?.takeIf { it.isNotBlank() }?.let { fmtDueShort(it) },
                        overdue = left != null && left < 0,
                        meta = buildList {
                            add(TeddMeta(task.sourceName()))
                            task.estimated_minutes?.let { add(TeddMeta("$it min", "focus")) }
                        },
                        priorityColor = if (task.priority == "high") TeddTokens.Danger else null,
                    )
                }
                if (tasks.size > 6) {
                    Text(
                        "et ${tasks.size - 6} de plus dans la liste du jour",
                        Modifier.padding(start = TeddSpace.S3, top = TeddSpace.S2),
                        color = TeddTokens.InkMuted,
                        style = TeddType.Caption,
                    )
                }
            }
        }
    }
}

@Composable
private fun TodayFocusTile(model: TodayModel, onOpen: () -> Unit, modifier: Modifier) {
    TeddCard(
        modifier = modifier,
        title = "Focus",
        icon = "focus",
        iconColor = TeddTokens.ModFocus,
        action = if (model.focusMinutesToday > 0) ({ TeddTag("Aujourd'hui", icon = "check") }) else null,
    ) {
        Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Text("${model.focusMinutesToday}", color = TeddTokens.Ink, style = TeddType.AmountLg)
            Text("min aujourd'hui", Modifier.padding(bottom = 5.dp), color = TeddTokens.InkMuted, style = TeddType.Caption)
            Spacer(Modifier.weight(1f))
            Text("${model.focusMin7.sum()} min sur 7 jours", Modifier.padding(bottom = 5.dp), color = TeddTokens.InkMuted, style = TeddType.MonoSm)
        }
        FocusMinutesBars(minutes = model.focusMin7, labels = model.weekLabels, height = 72.dp)
        TeddButton("Démarrer une session", onOpen, Modifier.fillMaxWidth(), icon = "play")
    }
}

@Composable
private fun TodayFinanceTile(
    model: TodayModel,
    hidden: Boolean,
    onToggleHidden: () -> Unit,
    onOpen: () -> Unit,
    modifier: Modifier,
) {
    val account = model.courant
    val (income, expense) = model.monthStats
    TeddCard(
        modifier = modifier,
        tone = TeddCardTone.Sunken,
        title = "Finance",
        icon = "finance",
        iconColor = TeddTokens.ModFinance,
        action = {
            TeddIconButton(onClick = onToggleHidden) {
                TeddIcon(if (hidden) "eye-off" else "eye", size = 16.dp, color = TeddTokens.InkMuted)
            }
        },
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
            Text(account?.name ?: "Compte courant", color = TeddTokens.InkMuted, style = TeddType.Caption)
            if (hidden) {
                Text("• • • • •", color = TeddTokens.Ink, style = TeddType.AmountLg)
            } else {
                TeddAmount(account?.balance ?: 0L, size = TeddAmountSize.Lg)
            }
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S5)) {
            MonthFigure("Entrées · ${currentMonthName()}", income, hidden, Modifier.weight(1f), signed = true)
            MonthFigure("Sorties · ${currentMonthName()}", -expense, hidden, Modifier.weight(1f))
        }
        Spacer(Modifier.weight(1f))
        TeddButton("Ouvrir Finance", onOpen, Modifier.fillMaxWidth())
    }
}

@Composable
private fun MonthFigure(label: String, value: Long, hidden: Boolean, modifier: Modifier, signed: Boolean = false) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(label, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1)
        if (hidden) Text("• • •", color = TeddTokens.Ink, style = TeddType.Amount)
        else TeddAmount(value, signed = signed)
    }
}

@Composable
private fun TodayNumbersTile(model: TodayModel, onReview: () -> Unit, onHabits: () -> Unit, modifier: Modifier) {
    TeddCard(modifier = modifier, title = "Le jour en chiffres", icon = "trend", iconColor = TeddTokens.ModHabits) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Figure("Envies traversées", if (model.crossingsToday > 0) model.crossingsToday.toString() else "—")
            Figure("Décisions réfléchies ce mois", model.rationalRateMonth?.let { "$it %" } ?: "—")
            Figure("Cartes à réviser", if (model.cardsDue > 0) model.cardsDue.toString() else "—")
        }
        Spacer(Modifier.weight(1f))
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            if (model.cardsDue > 0) TeddButton("Réviser", onReview, Modifier.weight(1f), icon = "cards")
            TeddButton("J'ai une envie", onHabits, Modifier.weight(1f), kind = TeddButtonKind.Ghost)
        }
    }
}

@Composable
private fun Figure(label: String, value: String) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        Text(label, Modifier.weight(1f), color = TeddTokens.InkMuted, style = TeddType.Body)
        Text(value, color = TeddTokens.Ink, style = TeddType.Amount)
    }
}
